import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { User, LogOut, ArrowLeft, Shield, Settings, ChevronRight, Lock, Save, Eye, EyeOff, Plus, Edit2, X, Trash2, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "./components/ui/button";
import { AuthModal } from "./components/AuthModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

// LAUSZ szerinti hivatalos személyek szerepei
const ROLES = [
  { id: "vezetobiro", name: "Vezetőbíró" },
  { id: "biro", name: "Bíró" },
  { id: "stilusbiro", name: "Stílusbíró" },
  { id: "dijlovasbiro", name: "Díjlovas bíró" },
  { id: "lovastusabiro", name: "Lovastusa bíró" },
  { id: "melegitobiro", name: "Melegítőpálya bíró" },
  { id: "versenyszervezo", name: "Versenyszervező" },
  { id: "palyaepito", name: "Pályaépítő" },
  { id: "versenyiroda", name: "Versenyiroda" }
];

const GENDERS = [
  { id: "kanca", name: "Kanca" },
  { id: "herelt", name: "Herélt" },
  { id: "men", name: "Mén" }
];

const getRoleLabel = (roleId) => {
  const role = ROLES.find(r => r.id === roleId);
  return role ? role.name : roleId;
};

const getGenderLabel = (genderId) => {
  const gender = GENDERS.find(g => g.id === genderId);
  return gender ? gender.name : genderId;
};

export default function Tag() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupToken = searchParams.get('setup');
  
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem("comp_token");
    return (stored && stored !== "undefined" && stored !== "null") ? stored : "";
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [activeTab, setActiveTab] = useState("profil");
  const [allUsers, setAllUsers] = useState([]);
  const [usersForSelection, setUsersForSelection] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editedRoles, setEditedRoles] = useState([]);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Setup password state
  const [setupInfo, setSetupInfo] = useState(null);
  const [setupPassword, setSetupPassword] = useState("");
  const [setupPasswordConfirm, setSetupPasswordConfirm] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  
  // Settings state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  // Horse form state
  const [showHorseForm, setShowHorseForm] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [horseForm, setHorseForm] = useState({
    name: "", gender: "", hun_id: "", color: "", 
    birth_year: "", birth_month: "", birth_day: "", owner_id: ""
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  
  // Child account & linking state
  const [showChildForm, setShowChildForm] = useState(false);
  const [childForm, setChildForm] = useState({ name: "", birth_date: "" });
  const [creatingChild, setCreatingChild] = useState(false);
  const [showCompletionEmailModal, setShowCompletionEmailModal] = useState(false);
  const [completionEmail, setCompletionEmail] = useState("");
  const [sendingCompletionEmail, setSendingCompletionEmail] = useState(false);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [linkIdentifier, setLinkIdentifier] = useState("");
  
  // Admin user creation state
  const [showAdminUserForm, setShowAdminUserForm] = useState(false);
  const [adminUserForm, setAdminUserForm] = useState({ name: "", email: "", birth_date: "", phone: "" });
  const [creatingAdminUser, setCreatingAdminUser] = useState(false);
  const [linkingAccount, setLinkingAccount] = useState(false);

  // Create child account
  const handleCreateChild = async () => {
    if (!childForm.name || !childForm.birth_date) {
      toast.error("Név és születési dátum kötelező!");
      return;
    }
    setCreatingChild(true);
    try {
      const res = await axios.post(`${API}/competition/users/child?token=${token}`, childForm);
      toast.success(`Gyerek fiók létrehozva! Azonosító: ${res.data.identifier}`);
      setShowChildForm(false);
      setChildForm({ name: "", birth_date: "" });
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
    setCreatingChild(false);
  };

  // Send completion email
  const handleSendCompletionEmail = async () => {
    if (!completionEmail) {
      toast.error("Email cím megadása kötelező!");
      return;
    }
    setSendingCompletionEmail(true);
    try {
      await axios.post(`${API}/competition/users/${selectedUserId}/send-completion-email?token=${token}`, { email: completionEmail });
      toast.success("Aktiváló email elküldve!");
      setShowCompletionEmailModal(false);
      setCompletionEmail("");
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
    setSendingCompletionEmail(false);
  };

  // Create full user by admin (no password - user will set it via email)
  const handleCreateAdminUser = async () => {
    if (!adminUserForm.name || !adminUserForm.email || !adminUserForm.birth_date) {
      toast.error("Név, email és születési dátum kötelező!");
      return;
    }
    setCreatingAdminUser(true);
    try {
      const res = await axios.post(`${API}/competition/users/admin-create?token=${token}`, adminUserForm);
      toast.success(`Felhasználó létrehozva! Azonosító: ${res.data.identifier}. Jelszó beállító email elküldve.`);
      setShowAdminUserForm(false);
      setAdminUserForm({ name: "", email: "", birth_date: "", phone: "" });
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
    setCreatingAdminUser(false);
  };

  // Fetch linked accounts
  const fetchLinkedAccounts = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/users/linked-accounts?token=${token}`);
      setLinkedAccounts(res.data.linked_accounts || []);
    } catch (err) {
      console.error("Error fetching linked accounts:", err);
    }
  };

  // Link account
  const handleLinkAccount = async () => {
    if (!linkIdentifier) {
      toast.error("Azonosító megadása kötelező!");
      return;
    }
    setLinkingAccount(true);
    try {
      const res = await axios.post(`${API}/competition/users/link-account?token=${token}`, { identifier: linkIdentifier });
      toast.success(res.data.message);
      setLinkIdentifier("");
      fetchLinkedAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
    setLinkingAccount(false);
  };

  // Unlink account
  const handleUnlinkAccount = async (linkedId) => {
    if (!confirm("Biztosan leválasztod ezt a fiókot?")) return;
    try {
      await axios.delete(`${API}/competition/users/unlink-account/${linkedId}?token=${token}`);
      toast.success("Fiók leválasztva");
      fetchLinkedAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  // Delete user function
  const handleDeleteUser = async (userId) => {
    if (!confirm("Biztosan törölni szeretnéd ezt a felhasználót? Ez a művelet nem visszavonható és törli az összes kapcsolódó adatot (lovak, nevezések, stb.)!")) return;
    
    setDeletingUser(true);
    try {
      const res = await axios.delete(`${API}/competition/users/${userId}?token=${token}`);
      toast.success(res.data.message || "Felhasználó törölve!");
      setSelectedUserId(null);
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba a törlés során");
    }
    setDeletingUser(false);
  };

  // Fetch setup info (for password setup page)
  const fetchSetupInfo = useCallback(async () => {
    if (!setupToken) return;
    setSetupLoading(true);
    try {
      const res = await axios.get(`${API}/competition/users/setup/${setupToken}`);
      setSetupInfo(res.data);
      setSetupError("");
    } catch (err) {
      setSetupError(err.response?.data?.detail || "Érvénytelen vagy lejárt link");
    }
    setSetupLoading(false);
  }, [setupToken]);

  // Submit password setup
  const handleSetupPassword = async () => {
    if (setupPassword.length < 6) {
      setSetupError("A jelszó minimum 6 karakter!");
      return;
    }
    if (setupPassword !== setupPasswordConfirm) {
      setSetupError("A két jelszó nem egyezik!");
      return;
    }
    setSetupLoading(true);
    setSetupError("");
    try {
      await axios.post(`${API}/competition/users/setup/${setupToken}`, { password: setupPassword });
      setSetupSuccess(true);
    } catch (err) {
      setSetupError(err.response?.data?.detail || "Hiba történt");
    }
    setSetupLoading(false);
  };

  // Effect to fetch setup info
  useEffect(() => {
    if (setupToken) {
      fetchSetupInfo();
    }
  }, [setupToken, fetchSetupInfo]);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/competition/me?token=${token}`);
      setUser(res.data);
      setProfileForm({ name: res.data.name || "", phone: res.data.phone || "" });
    } catch {
      localStorage.removeItem("comp_token");
      setToken("");
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  const fetchAllUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/users?token=${token}`);
      setAllUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  }, [token]);

  const fetchUsersForSelection = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/users-for-selection?token=${token}`);
      setUsersForSelection(res.data || []);
    } catch (err) {
      console.error("Failed to fetch users for selection", err);
    }
  }, [token]);

  const fetchHorses = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/competition/horses?token=${token}`);
      setHorses(res.data || []);
    } catch (err) {
      console.error("Failed to fetch horses", err);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (user) {
      fetchHorses();
      fetchUsersForSelection();
      fetchLinkedAccounts();
      if (user.is_admin) fetchAllUsers();
    }
  }, [user, fetchHorses, fetchUsersForSelection, fetchAllUsers]);

  const handleLogout = () => {
    localStorage.removeItem("comp_token");
    setToken("");
    setUser(null);
    navigate("/");
  };

  const updateUserRole = (userId, roleId, shouldAdd) => {
    if (shouldAdd) {
      setEditedRoles(prev => [...prev, roleId]);
    } else {
      setEditedRoles(prev => prev.filter(r => r !== roleId));
    }
  };

  const selectUserForEdit = (userId) => {
    const selectedUser = allUsers.find(u => u.id === userId);
    setSelectedUserId(userId);
    setEditedRoles(selectedUser?.roles || []);
  };

  const saveUserRoles = async () => {
    if (!selectedUserId) return;
    setRolesSaving(true);
    try {
      await axios.put(`${API}/competition/users/${selectedUserId}/roles?token=${token}`, {
        roles: editedRoles
      });
      await fetchAllUsers();
      alert("Jogosultságok mentve!");
    } catch (err) {
      alert("Hiba történt a mentés során");
    }
    setRolesSaving(false);
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("Az új jelszavak nem egyeznek!");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      alert("Az új jelszó legalább 8 karakter legyen!");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/competition/change-password?token=${token}`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      alert("Jelszó sikeresen megváltoztatva!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      alert(err.response?.data?.detail || "Hiba a jelszó módosításakor");
    }
    setSaving(false);
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/competition/profile?token=${token}`, {
        name: profileForm.name,
        phone: profileForm.phone
      });
      await fetchUser();
      alert("Profil mentve!");
    } catch (err) {
      alert("Hiba a profil mentésekor");
    }
    setSaving(false);
  };

  // Horse management
  const resetHorseForm = () => {
    setHorseForm({
      name: "", gender: "", hun_id: "", color: "", 
      birth_year: "", birth_month: "", birth_day: "", owner_id: user?.id || ""
    });
    setEditingHorse(null);
    setShowHorseForm(false);
  };

  const openNewHorseForm = () => {
    setHorseForm({
      name: "", gender: "", hun_id: "", color: "", 
      birth_year: "", birth_month: "", birth_day: "", owner_id: user?.id || ""
    });
    setEditingHorse(null);
    setShowHorseForm(true);
  };

  const openEditHorseForm = (horse) => {
    setHorseForm({
      name: horse.name || "",
      gender: horse.gender || "",
      hun_id: horse.hun_id || "",
      color: horse.color || "",
      birth_year: horse.birth_year || "",
      birth_month: horse.birth_month || "",
      birth_day: horse.birth_day || "",
      owner_id: horse.owner_id || ""
    });
    setEditingHorse(horse);
    setShowHorseForm(true);
  };

  const handleSaveHorse = async () => {
    if (!horseForm.name) {
      alert("A ló neve kötelező!");
      return;
    }
    setSaving(true);
    try {
      if (editingHorse) {
        // Update existing horse (admin only)
        await axios.put(`${API}/competition/horses/${editingHorse.id}?token=${token}`, {
          name: horseForm.name,
          gender: horseForm.gender,
          hun_id: horseForm.hun_id,
          color: horseForm.color,
          birth_year: parseInt(horseForm.birth_year) || 0,
          birth_month: parseInt(horseForm.birth_month) || 0,
          birth_day: parseInt(horseForm.birth_day) || 0,
          owner_id: horseForm.owner_id
        });
        alert("Ló adatai frissítve!");
      } else {
        // Create new horse
        await axios.post(`${API}/competition/horses?token=${token}`, {
          name: horseForm.name,
          gender: horseForm.gender,
          hun_id: horseForm.hun_id,
          color: horseForm.color,
          birth_year: parseInt(horseForm.birth_year) || 0,
          birth_month: parseInt(horseForm.birth_month) || 0,
          birth_day: parseInt(horseForm.birth_day) || 0,
          owner_id: horseForm.owner_id || user?.id
        });
        alert("Ló sikeresen hozzáadva!");
      }
      await fetchHorses();
      resetHorseForm();
    } catch (err) {
      alert(err.response?.data?.detail || "Hiba történt a mentés során");
    }
    setSaving(false);
  };

  const handleDeleteHorse = async () => {
    if (!editingHorse) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/competition/horses/${editingHorse.id}?token=${token}`);
      alert("Ló sikeresen törölve!");
      await fetchHorses();
      resetHorseForm();
      setShowDeleteConfirm(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Hiba történt a törlés során");
    }
    setSaving(false);
  };

  // Get horses for display
  const getDisplayHorses = () => {
    if (user?.is_admin) {
      // Admin sees all horses alphabetically
      return [...horses].sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu'));
    } else {
      // Regular user sees only their own horses alphabetically
      return horses.filter(h => h.owner_id === user?.id).sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Password setup page (from email link)
  if (setupToken) {
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Gepárd" className="w-10 h-10 object-contain" />
              <span className="text-lg font-semibold text-slate-800">Gepárd Lovas Egyesület</span>
            </div>
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-12">
          {setupLoading && !setupInfo && (
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Betöltés...</p>
            </div>
          )}
          
          {setupError && !setupInfo && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-red-800 mb-2">Hiba</h2>
              <p className="text-red-600">{setupError}</p>
              <Button onClick={() => navigate("/ugyfelkapu")} className="mt-4 bg-slate-600 hover:bg-slate-700 text-white">
                Vissza a bejelentkezéshez
              </Button>
            </div>
          )}
          
          {setupSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-green-800 mb-2">Sikeres beállítás!</h2>
              <p className="text-green-600 mb-4">A jelszavad beállítva. Most már bejelentkezhetsz.</p>
              <Button onClick={() => navigate("/ugyfelkapu")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Bejelentkezés
              </Button>
            </div>
          )}
          
          {setupInfo && !setupSuccess && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">Jelszó beállítása</h2>
              <p className="text-slate-500 text-center mb-6">Állítsd be a jelszavadat a fiókodhoz</p>
              
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-800 mb-2">Fiók adatai</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-600">Név:</span> <span className="font-medium text-slate-800">{setupInfo.name}</span></p>
                  <p><span className="text-slate-600">Email:</span> <span className="font-medium text-slate-800">{setupInfo.email}</span></p>
                  <p><span className="text-slate-600">Azonosító:</span> <span className="font-medium text-slate-800">{setupInfo.identifier}</span></p>
                  <p><span className="text-slate-600">Születési dátum:</span> <span className="font-medium text-slate-800">{setupInfo.birth_date}</span></p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Új jelszó *</label>
                  <div className="relative">
                    <input
                      type={showSetupPassword ? "text" : "password"}
                      value={setupPassword}
                      onChange={(e) => setSetupPassword(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg p-3 pr-10 text-slate-800"
                      placeholder="Min. 6 karakter"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSetupPassword(!showSetupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showSetupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Jelszó megerősítése *</label>
                  <input
                    type={showSetupPassword ? "text" : "password"}
                    value={setupPasswordConfirm}
                    onChange={(e) => setSetupPasswordConfirm(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                    placeholder="Írd be újra a jelszót"
                  />
                </div>
                
                {setupError && (
                  <p className="text-red-600 text-sm">{setupError}</p>
                )}
                
                <Button 
                  onClick={handleSetupPassword} 
                  disabled={setupLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {setupLoading ? "Mentés..." : "Jelszó beállítása"}
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Gepárd" className="w-10 h-10 object-contain" />
              <span className="text-lg font-semibold text-slate-800">Gepárd Lovas Egyesület</span>
            </div>
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-12 text-center">
          <User size={64} className="mx-auto text-slate-400 mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Profil</h1>
          <p className="text-slate-500 mb-6">Jelentkezz be a profilod megtekintéséhez</p>
          <Button onClick={() => setShowAuthModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            Bejelentkezés
          </Button>
        </main>
        <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} onLoginSuccess={(userData, token) => {
          setToken(token);
          localStorage.setItem("comp_token", token);
          setShowAuthModal(false);
        }} />
      </div>
    );
  }

  const displayHorses = getDisplayHorses();

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="Gepárd" className="w-10 h-10 object-contain" />
            <span className="text-lg font-semibold text-slate-800">Gepárd Lovas Egyesület</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-slate-600">
            <LogOut size={18} className="mr-1" /> Kijelentkezés
          </Button>
        </div>
      </header>

      {/* Quick Navigation Buttons */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
        <div className="max-w-5xl mx-auto flex gap-3">
          <Button 
            onClick={() => navigate("/verseny")} 
            className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
          >
            🏇 Versenyek
          </Button>
          <Button 
            onClick={() => navigate("/shop")} 
            className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
          >
            🛒 Bolt
          </Button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
              {user.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-800">{user.name}</h1>
              <p className="text-slate-500">{user.email}</p>
              {user.roles?.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {user.roles.map(r => (
                    <span key={r} className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                      {getRoleLabel(r)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab("profil")}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap flex items-center gap-2 ${
              activeTab === "profil" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <User size={18} /> Profil
          </button>
          <button
            onClick={() => setActiveTab("lovak")}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap flex items-center gap-2 ${
              activeTab === "lovak" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            🐴 {user.is_admin ? "Lovak" : "Lovaim"}
          </button>
          <button
            onClick={() => setActiveTab("beallitasok")}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap flex items-center gap-2 ${
              activeTab === "beallitasok" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Settings size={18} /> Beállítások
          </button>
          {user.is_admin && (
            <button
              onClick={() => setActiveTab("felhasznalok")}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap flex items-center gap-2 ${
                activeTab === "felhasznalok" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Shield size={18} /> Felhasználók
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          {activeTab === "profil" && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Profil adatok</h2>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Név</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                    className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                  <div className="bg-slate-100 rounded-lg p-3 text-slate-600">{user.email}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Telefonszám</label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                    className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                    placeholder="+36 30 123 4567"
                  />
                </div>
                <Button onClick={handleProfileUpdate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Save size={18} className="mr-2" /> {saving ? "Mentés..." : "Profil mentése"}
                </Button>
              </div>
              
              {/* Linked Accounts Section */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Hozzárendelt fiókok</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Add meg egy másik felhasználó azonosítóját (pl. AB1234), hogy hozzárendeld a fiókodhoz. 
                  A hozzárendelt fiókokat is be tudod nevezni versenyekre.
                </p>
                
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={linkIdentifier}
                    onChange={(e) => setLinkIdentifier(e.target.value.toUpperCase())}
                    placeholder="Azonosító (pl. AB1234)"
                    className="flex-1 border border-slate-200 rounded-lg p-3 text-slate-800 uppercase"
                    maxLength={6}
                  />
                  <Button onClick={handleLinkAccount} disabled={linkingAccount} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    {linkingAccount ? "..." : "Hozzáadás"}
                  </Button>
                </div>
                
                {linkedAccounts.length > 0 ? (
                  <div className="space-y-2">
                    {linkedAccounts.map(la => (
                      <div key={la.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-800">{la.name}</p>
                          <p className="text-sm text-slate-500">Azonosító: {la.identifier} • Szül.: {la.birth_date}</p>
                        </div>
                        <button onClick={() => handleUnlinkAccount(la.id)} className="text-red-500 hover:text-red-700">
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">Nincs hozzárendelt fiók</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "lovak" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">
                  {user.is_admin ? `Lovak (${displayHorses.length})` : "Lovaim"}
                </h2>
                <Button onClick={openNewHorseForm} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <Plus size={18} className="mr-1" /> Új ló
                </Button>
              </div>

              {/* Horse Form Modal */}
              {showHorseForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">
                        {editingHorse ? "Ló szerkesztése" : "Új ló hozzáadása"}
                      </h3>
                      <button onClick={resetHorseForm} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Ló neve *</label>
                        <input
                          type="text"
                          value={horseForm.name}
                          onChange={(e) => setHorseForm({...horseForm, name: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="Pl. Villám"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Nem</label>
                          <select
                            value={horseForm.gender}
                            onChange={(e) => setHorseForm({...horseForm, gender: e.target.value})}
                            className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white"
                          >
                            <option value="">Válassz...</option>
                            {GENDERS.map(g => (
                              <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-600 mb-1">Szín</label>
                          <input
                            type="text"
                            value={horseForm.color}
                            onChange={(e) => setHorseForm({...horseForm, color: e.target.value})}
                            className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                            placeholder="Pl. Pej"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">HUN azonosító</label>
                        <input
                          type="text"
                          value={horseForm.hun_id}
                          onChange={(e) => setHorseForm({...horseForm, hun_id: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="Pl. HUN12345"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Születési dátum</label>
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="number"
                            value={horseForm.birth_year}
                            onChange={(e) => setHorseForm({...horseForm, birth_year: e.target.value})}
                            className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                            placeholder="Év"
                            min="1980"
                            max={new Date().getFullYear()}
                          />
                          <input
                            type="number"
                            value={horseForm.birth_month}
                            onChange={(e) => setHorseForm({...horseForm, birth_month: e.target.value})}
                            className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                            placeholder="Hónap"
                            min="1"
                            max="12"
                          />
                          <input
                            type="number"
                            value={horseForm.birth_day}
                            onChange={(e) => setHorseForm({...horseForm, birth_day: e.target.value})}
                            className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                            placeholder="Nap"
                            min="1"
                            max="31"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Tulajdonos</label>
                        <select
                          value={horseForm.owner_id}
                          onChange={(e) => setHorseForm({...horseForm, owner_id: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800 bg-white"
                        >
                          <option value="">Válassz tulajdonost...</option>
                          {usersForSelection.map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.id.slice(-6)})</option>
                          ))}
                        </select>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-4">
                        <Button onClick={resetHorseForm} variant="outline" className="flex-1">
                          Mégse
                        </Button>
                        <Button onClick={handleSaveHorse} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                          {saving ? "Mentés..." : (editingHorse ? "Mentés" : "Hozzáadás")}
                        </Button>
                      </div>

                      {/* Delete button - only for editing existing horse (admin only) */}
                      {editingHorse && user?.is_admin && (
                        <div className="border-t pt-4 mt-4">
                          <Button 
                            onClick={() => setShowDeleteConfirm(true)} 
                            variant="outline" 
                            className="w-full border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={18} className="mr-2" /> Ló törlése
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && editingHorse && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                  <div className="bg-white rounded-2xl max-w-md w-full p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle size={24} className="text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Ló törlése</h3>
                        <p className="text-slate-500 text-sm">Ez a művelet nem visszavonható!</p>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <p className="text-slate-700">
                        Biztosan törölni szeretnéd a következő lovat?
                      </p>
                      <p className="font-semibold text-slate-800 mt-2">
                        🐴 {editingHorse.name} {editingHorse.hun_id && `(${editingHorse.hun_id})`}
                      </p>
                      {editingHorse.owner && (
                        <p className="text-sm text-slate-500 mt-1">Tulajdonos: {editingHorse.owner}</p>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        onClick={() => setShowDeleteConfirm(false)} 
                        variant="outline" 
                        className="flex-1"
                        disabled={saving}
                      >
                        Mégse
                      </Button>
                      <Button 
                        onClick={handleDeleteHorse} 
                        disabled={saving}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      >
                        {saving ? "Törlés..." : "Igen, törlés"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Horse List */}
              {displayHorses.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-5xl mb-3 block">🐴</span>
                  <p className="text-slate-500">
                    {user.is_admin ? "Még nincsenek lovak regisztrálva." : "Még nincsenek lovaid regisztrálva."}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">Kattints az Új ló gombra a hozzáadáshoz.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {displayHorses.map(horse => (
                    <div 
                      key={horse.id} 
                      className={`p-4 bg-slate-50 rounded-lg flex items-center gap-4 ${user.is_admin ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                      onClick={() => user.is_admin && openEditHorseForm(horse)}
                    >
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl">
                        🐴
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">
                          {horse.name} {horse.hun_id && <span className="text-slate-500">({horse.hun_id})</span>}
                        </p>
                        <p className="text-sm text-slate-500">
                          {horse.gender && `${getGenderLabel(horse.gender)} • `}
                          {horse.color && `${horse.color} • `}
                          {horse.birth_year ? `${new Date().getFullYear() - horse.birth_year} éves` : ""}
                        </p>
                        {user.is_admin && horse.owner && (
                          <p className="text-xs text-blue-500 mt-1">Tulajdonos: {horse.owner}</p>
                        )}
                      </div>
                      {user.is_admin && (
                        <Edit2 size={18} className="text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "beallitasok" && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-6">Beállítások</h2>
              
              {/* Password Change */}
              <div className="mb-8">
                <h3 className="text-md font-medium text-slate-700 mb-4 flex items-center gap-2">
                  <Lock size={18} /> Jelszó módosítása
                </h3>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Jelenlegi jelszó</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                        className="w-full border border-slate-200 rounded-lg p-3 pr-10 text-slate-800"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Új jelszó</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                        className="w-full border border-slate-200 rounded-lg p-3 pr-10 text-slate-800"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Minimum 8 karakter</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Új jelszó megerősítése</label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                    />
                  </div>
                  <Button
                    onClick={handlePasswordChange}
                    disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {saving ? "Mentés..." : "Jelszó módosítása"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "felhasznalok" && user.is_admin && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">Felhasználók kezelése ({allUsers.length} felhasználó)</h2>
                <div className="flex gap-2">
                  <Button onClick={() => setShowAdminUserForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus size={18} className="mr-1" /> Felhasználó
                  </Button>
                  <Button onClick={() => setShowChildForm(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus size={18} className="mr-1" /> Gyerek fiók
                  </Button>
                </div>
              </div>
              
              {/* Child Account Creation Modal */}
              {showChildForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">Gyerek fiók létrehozása</h3>
                      <button onClick={() => setShowChildForm(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                      </button>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Hozz létre fiókot egy gyerek számára. A rendszer automatikusan generál azonosítót.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Név *</label>
                        <input
                          type="text"
                          value={childForm.name}
                          onChange={(e) => setChildForm({...childForm, name: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="Gyerek neve"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Születési dátum *</label>
                        <input
                          type="date"
                          value={childForm.birth_date}
                          onChange={(e) => setChildForm({...childForm, birth_date: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                        />
                      </div>
                      <Button onClick={handleCreateChild} disabled={creatingChild} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                        {creatingChild ? "Létrehozás..." : "Fiók létrehozása"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Full User Creation Modal */}
              {showAdminUserForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">Felhasználó hozzáadása</h3>
                      <button onClick={() => setShowAdminUserForm(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                      </button>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Hozz létre teljes fiókot. A felhasználó kap egy ellenőrző emailt a kóddal.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Név *</label>
                        <input
                          type="text"
                          value={adminUserForm.name}
                          onChange={(e) => setAdminUserForm({...adminUserForm, name: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="Teljes név"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Email *</label>
                        <input
                          type="email"
                          value={adminUserForm.email}
                          onChange={(e) => setAdminUserForm({...adminUserForm, email: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Születési dátum *</label>
                        <input
                          type="date"
                          value={adminUserForm.birth_date}
                          onChange={(e) => setAdminUserForm({...adminUserForm, birth_date: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Telefon</label>
                        <input
                          type="tel"
                          value={adminUserForm.phone}
                          onChange={(e) => setAdminUserForm({...adminUserForm, phone: e.target.value})}
                          className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                          placeholder="+36..."
                        />
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-700">A felhasználó emailben kapja meg a linket a jelszó beállításához. Nincs szükség megerősítő kódra.</p>
                      </div>
                      <Button onClick={handleCreateAdminUser} disabled={creatingAdminUser} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                        {creatingAdminUser ? "Létrehozás..." : "Felhasználó létrehozása"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {allUsers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nincs regisztrált felhasználó.</p>
              ) : selectedUserId ? (
                <div>
                  <button onClick={() => { setSelectedUserId(null); setEditedRoles([]); }} className="flex items-center gap-2 text-slate-600 mb-4 hover:text-blue-600">
                    <ArrowLeft size={18} /> Vissza
                  </button>
                  {(() => {
                    const selectedUser = allUsers.find(u => u.id === selectedUserId);
                    if (!selectedUser) return null;
                    return (
                      <div>
                        <h4 className="text-lg font-semibold text-slate-800 mb-2">{selectedUser.name}</h4>
                        <p className="text-slate-500 mb-2">{selectedUser.email || "Nincs email"}</p>
                        
                        {/* Incomplete account warning */}
                        {selectedUser.is_child_account && !selectedUser.is_complete && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <p className="text-amber-800 font-medium mb-2">⚠️ Nem aktivált fiók</p>
                            <p className="text-sm text-amber-600 mb-3">Ez a fiók még nincs aktiválva. Küldj aktiváló emailt a gyerek szülőjének.</p>
                            <Button onClick={() => setShowCompletionEmailModal(true)} className="bg-amber-500 hover:bg-amber-600 text-white">
                              Adatok hozzáadása (Email küldés)
                            </Button>
                          </div>
                        )}
                        
                        {/* Completion Email Modal */}
                        {showCompletionEmailModal && (
                          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-2xl max-w-md w-full p-6">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-slate-800">Aktiváló email küldése</h3>
                                <button onClick={() => setShowCompletionEmailModal(false)} className="text-slate-400 hover:text-slate-600">
                                  <X size={24} />
                                </button>
                              </div>
                              <p className="text-sm text-slate-500 mb-4">
                                Add meg a szülő email címét. Erre a címre küldjük az aktiváló linket, ahol megadhatják a jelszót és telefont.
                              </p>
                              <div className="space-y-4">
                                <input
                                  type="email"
                                  value={completionEmail}
                                  onChange={(e) => setCompletionEmail(e.target.value)}
                                  className="w-full border border-slate-200 rounded-lg p-3 text-slate-800"
                                  placeholder="szulo@email.com"
                                />
                                <Button onClick={handleSendCompletionEmail} disabled={sendingCompletionEmail} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                                  {sendingCompletionEmail ? "Küldés..." : "Email küldése"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* User Details */}
                        <div className="bg-slate-50 rounded-lg p-4 mb-6">
                          <h5 className="font-medium text-slate-700 mb-3">Felhasználó adatai</h5>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">Azonosító:</span>
                              <p className="font-mono text-slate-700 font-bold">{selectedUser.identifier || "Nincs"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Születési dátum:</span>
                              <p className="text-slate-700">{selectedUser.birth_date || "Nincs megadva"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Telefon:</span>
                              <p className="text-slate-700">{selectedUser.phone || "Nincs megadva"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Fiók típus:</span>
                              <p className="text-slate-700">{selectedUser.is_child_account ? "Gyerek fiók" : "Normál"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Státusz:</span>
                              <p className="text-slate-700">{selectedUser.is_complete ? "✅ Aktivált" : "⏳ Nincs aktiválva"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Admin:</span>
                              <p className="text-slate-700">{selectedUser.is_admin ? "✅ Igen" : "❌ Nem"}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Jogosultságok:</span>
                              <p className="text-slate-700">{selectedUser.roles?.length > 0 ? selectedUser.roles.map(r => getRoleLabel(r)).join(", ") : "Nincs"}</p>
                            </div>
                          </div>
                        </div>
                        
                        <h5 className="font-medium text-slate-700 mb-3">Jogosultságok szerkesztése</h5>
                        <div className="space-y-2 mb-6">
                          {ROLES.map(role => {
                            const hasRole = editedRoles.includes(role.id);
                            return (
                              <label key={role.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                                <input
                                  type="checkbox"
                                  checked={hasRole}
                                  onChange={() => updateUserRole(selectedUserId, role.id, !hasRole)}
                                  className="w-5 h-5 rounded border-slate-300"
                                />
                                <span className="text-slate-800">{role.name}</span>
                              </label>
                            );
                          })}
                        </div>
                        
                        <Button 
                          onClick={saveUserRoles} 
                          disabled={rolesSaving}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-4"
                        >
                          {rolesSaving ? "Mentés..." : "Jogosultságok mentése"}
                        </Button>
                        
                        {/* Delete User Button */}
                        <Button 
                          onClick={() => handleDeleteUser(selectedUserId)} 
                          disabled={deletingUser || selectedUser.is_admin}
                          variant="outline"
                          className="w-full border-red-500 text-red-500 hover:bg-red-50"
                        >
                          {deletingUser ? "Törlés..." : selectedUser.is_admin ? "Admin nem törölhető" : "🗑️ Felhasználó törlése"}
                        </Button>
                        {selectedUser.is_admin && (
                          <p className="text-xs text-slate-400 text-center mt-2">Admin felhasználók nem törölhetők biztonsági okokból.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-2">
                  {[...allUsers].sort((a, b) => (a.name || "").localeCompare(b.name || "", 'hu')).map(u => (
                    <button
                      key={u.id}
                      onClick={() => selectUserForEdit(u.id)}
                      className={`w-full p-4 rounded-lg hover:bg-slate-100 flex items-center justify-between text-left ${u.is_child_account ? 'bg-purple-50 border-l-4 border-purple-400' : 'bg-slate-50'}`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800">{u.name}</p>
                          {u.is_child_account && (
                            <span className="px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full font-medium">Gyerek</span>
                          )}
                          {!u.is_complete && (
                            <span className="px-2 py-0.5 bg-yellow-200 text-yellow-700 text-xs rounded-full font-medium">Hiányos</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {u.identifier && <span className="font-mono mr-2">{u.identifier}</span>}
                          {u.email || (u.is_child_account ? "Nincs email" : "")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.roles?.map(r => (
                          <span key={r} className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                            {getRoleLabel(r)}
                          </span>
                        ))}
                        <ChevronRight size={18} className="text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={(t) => {
        setToken(t);
        localStorage.setItem("comp_token", t);
        setShowAuthModal(false);
      }} />
    </div>
  );
}
